import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  IconButton,
  Paper,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import {
  addPolicyCBException,
  deletePolicyCBException,
  editPolicyCBException,
  getPolicyCBExceptionById,
  getPolicyCBExceptions,
} from '../../../../api/endpoints/corporate';
import type { CorporatePolicyCBException } from '../../../../api/types/corporate';
import { ApiErrorAlert } from '../../../../components/ApiErrorAlert';
import { ConfirmDialog } from '../../../../components/ConfirmDialog';
import { useFreshEditRow } from '../../../../components/useFreshEditRow';

const schema = z.object({
  country: z.string().optional(),
  countryCode: z.string().optional(),
  roundTrip: z.boolean(),
  maxBudget: z.coerce.number().min(0),
  businessClass: z.boolean(),
  firstClass: z.boolean(),
});

type FormValues = z.infer<typeof schema>;

const emptyValues: FormValues = {
  country: '',
  countryCode: '',
  roundTrip: false,
  maxBudget: 0,
  businessClass: false,
  firstClass: false,
};

function toFormValues(row: CorporatePolicyCBException): FormValues {
  return {
    country: row.country ?? '',
    countryCode: row.countryCode ?? '',
    roundTrip: row.roundTrip ?? false,
    maxBudget: row.maxBudget ?? 0,
    businessClass: row.businessClass ?? false,
    firstClass: row.firstClass ?? false,
  };
}

/** Kabin sınıfı (ülke bazlı) istisnaları — Policy/*PolicyCBException routes. */
export function CBExceptionsPanel({ policyID }: { policyID: number }) {
  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState<CorporatePolicyCBException | null>(null);
  const [deleting, setDeleting] = useState<CorporatePolicyCBException | null>(null);
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const queryKey = ['corporate', 'policyCBExceptions', policyID];

  const query = useQuery({ queryKey, queryFn: () => getPolicyCBExceptions(policyID) });
  const rows: CorporatePolicyCBException[] = query.data?.corporateFlightExceptionsCB ?? [];

  const deleteMutation = useMutation({
    mutationFn: (id: number) => deletePolicyCBException(id),
    onSuccess: () => {
      enqueueSnackbar('İstisna silindi.', { variant: 'success' });
      queryClient.invalidateQueries({ queryKey });
      setDeleting(null);
    },
    onError: (err) => enqueueSnackbar(err instanceof Error ? err.message : 'İşlem başarısız.', { variant: 'error' }),
  });

  return (
    <Box>
      <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="subtitle1">Kabin Sınıfı İstisnaları (Ülke Bazlı)</Typography>
        <Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={() => setCreateOpen(true)}>
          Yeni İstisna
        </Button>
      </Stack>

      {query.isError && <ApiErrorAlert error={query.error} sx={{ mb: 2 }} />}
      {query.isFetching && <CircularProgress size={20} sx={{ mb: 2 }} />}

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Ülke</TableCell>
              <TableCell>Kod</TableCell>
              <TableCell>Gidiş-Dönüş</TableCell>
              <TableCell>Maks. Bütçe</TableCell>
              <TableCell>Business</TableCell>
              <TableCell>First</TableCell>
              <TableCell align="right" />
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length === 0 && !query.isFetching && (
              <TableRow>
                <TableCell colSpan={7}>
                  <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                    Kayıtlı istisna bulunamadı.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {rows.map((row) => (
              <TableRow key={row.flightException2ID} hover>
                <TableCell>{row.country ?? '—'}</TableCell>
                <TableCell>{row.countryCode ?? '—'}</TableCell>
                <TableCell>{row.roundTrip ? 'Evet' : 'Hayır'}</TableCell>
                <TableCell>{row.maxBudget}</TableCell>
                <TableCell>{row.businessClass ? 'Evet' : 'Hayır'}</TableCell>
                <TableCell>{row.firstClass ? 'Evet' : 'Hayır'}</TableCell>
                <TableCell align="right">
                  <IconButton size="small" onClick={() => setEditing(row)} aria-label="Düzenle">
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton size="small" onClick={() => setDeleting(row)} aria-label="Sil">
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <CBExceptionFormDialog
        open={createOpen}
        policyID={policyID}
        row={null}
        onClose={() => setCreateOpen(false)}
        queryKey={queryKey}
      />
      <CBExceptionFormDialog
        open={!!editing}
        policyID={policyID}
        row={editing}
        onClose={() => setEditing(null)}
        queryKey={queryKey}
      />

      <ConfirmDialog
        open={!!deleting}
        title="İstisnayı sil"
        description="Bu kabin sınıfı istisnasını silmek istediğinize emin misiniz?"
        loading={deleteMutation.isPending}
        onConfirm={() => deleting && deleteMutation.mutate(deleting.flightException2ID)}
        onCancel={() => setDeleting(null)}
      />
    </Box>
  );
}

function CBExceptionFormDialog({
  open,
  policyID,
  row,
  onClose,
  queryKey,
}: {
  open: boolean;
  policyID: number;
  row: CorporatePolicyCBException | null;
  onClose: () => void;
  queryKey: (string | number)[];
}) {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const isEdit = !!row;

  const {
    control,
    handleSubmit,
    register,
    reset,
  } = useForm<FormValues>({ resolver: zodResolver(schema), defaultValues: emptyValues });

  // Refetch the fresh row on edit-open; silently falls back to the list row on error.
  const seed = useFreshEditRow({
    open,
    editing: row,
    queryKey: ['corporate', 'policyCBException', row?.flightException2ID],
    fetch: async (r) => (await getPolicyCBExceptionById(r.flightException2ID)).corporateFlightExceptionsCB?.[0],
  });

  useEffect(() => {
    if (open) reset(seed ? toFormValues(seed) : emptyValues);
  }, [open, seed, reset]);

  const mutation = useMutation({
    mutationFn: (values: FormValues) =>
      isEdit && row
        ? editPolicyCBException({ ...values, policyID, flightException2ID: row.flightException2ID })
        : addPolicyCBException({ ...values, policyID }),
    onSuccess: () => {
      enqueueSnackbar(isEdit ? 'İstisna güncellendi.' : 'İstisna oluşturuldu.', { variant: 'success' });
      queryClient.invalidateQueries({ queryKey });
      onClose();
    },
    onError: () => {
      /* surfaced via ApiErrorAlert below */
    },
  });

  const submit = handleSubmit((values) => mutation.mutate(values));

  return (
    <Dialog open={open} onClose={() => !mutation.isPending && onClose()} maxWidth="sm" fullWidth>
      <DialogTitle>{isEdit ? 'İstisnayı Güncelle' : 'Yeni Kabin Sınıfı İstisnası'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField fullWidth size="small" label="Ülke" {...register('country')} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField fullWidth size="small" label="Ülke Kodu" {...register('countryCode')} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField fullWidth size="small" type="number" label="Maks. Bütçe" {...register('maxBudget')} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Controller
                control={control}
                name="roundTrip"
                render={({ field }) => (
                  <FormControlLabel
                    control={<Switch checked={!!field.value} onChange={(e) => field.onChange(e.target.checked)} />}
                    label="Gidiş-Dönüş"
                  />
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Controller
                control={control}
                name="businessClass"
                render={({ field }) => (
                  <FormControlLabel
                    control={<Switch checked={!!field.value} onChange={(e) => field.onChange(e.target.checked)} />}
                    label="Business Class"
                  />
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Controller
                control={control}
                name="firstClass"
                render={({ field }) => (
                  <FormControlLabel
                    control={<Switch checked={!!field.value} onChange={(e) => field.onChange(e.target.checked)} />}
                    label="First Class"
                  />
                )}
              />
            </Grid>
          </Grid>
          <ApiErrorAlert error={mutation.error} />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={mutation.isPending}>
          Vazgeç
        </Button>
        <Button onClick={submit} variant="contained" disabled={mutation.isPending}>
          {mutation.isPending ? 'Kaydediliyor…' : 'Kaydet'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
